// Serialize/deserialize the subset of settings surfaced by the canvas
// display popover (Image grid, Color settings, Image quality, Depth
// visualization) for persistence at the project level.

import { GridColor } from 'reducers';
import { ChunkQuality } from 'cvat-core-wrapper';
import { ImageFilter, ImageFilterAlias } from 'utils/image-processing';
import GammaCorrection from 'utils/fabric-wrapper/gamma-correciton';
import {
    DepthVisualizationOptions,
    createDepthVisualizationFilter,
} from 'components/annotation-page/canvas/views/canvas2d/depth-visualization-filter';

export interface DisplaySettingsPlayer {
    grid: boolean;
    gridSize: number;
    gridColor: GridColor;
    gridOpacity: number;
    brightnessLevel: number;
    contrastLevel: number;
    saturationLevel: number;
}

export interface SerializedImageFilter {
    alias: ImageFilterAlias;
    options: Record<string, any>;
}

export interface DisplaySettings {
    player: DisplaySettingsPlayer;
    imageFilters: SerializedImageFilter[];
    chunkQuality: ChunkQuality;
}

export const defaultDisplaySettings: DisplaySettings = {
    player: {
        grid: false,
        gridSize: 100,
        gridColor: GridColor.White,
        gridOpacity: 100,
        brightnessLevel: 100,
        contrastLevel: 100,
        saturationLevel: 100,
    },
    imageFilters: [],
    chunkQuality: ChunkQuality.COMPRESSED,
};

export function hydrateImageFilter(serialized: SerializedImageFilter): ImageFilter | null {
    if (serialized.alias === ImageFilterAlias.GAMMA_CORRECTION) {
        const g = serialized.options.gamma;
        const gammaArray = Array.isArray(g) ? g : [g, g, g];
        return {
            modifier: new GammaCorrection({ gamma: gammaArray }),
            alias: ImageFilterAlias.GAMMA_CORRECTION,
        };
    }
    if (serialized.alias === ImageFilterAlias.DEPTH_VISUALIZATION) {
        return createDepthVisualizationFilter(serialized.options as DepthVisualizationOptions);
    }
    return null;
}

export function normalizeDisplaySettings(raw: Record<string, any> | null | undefined): DisplaySettings {
    if (!raw || typeof raw !== 'object') return { ...defaultDisplaySettings };
    const merged: DisplaySettings = {
        player: { ...defaultDisplaySettings.player, ...(raw.player ?? {}) },
        imageFilters: Array.isArray(raw.imageFilters) ? raw.imageFilters : [],
        chunkQuality: raw.chunkQuality === ChunkQuality.ORIGINAL ? ChunkQuality.ORIGINAL : ChunkQuality.COMPRESSED,
    };
    return merged;
}
